(() => {
  'use strict';

  const FOCUSABLE = 'a[href],button:enabled,input:not([disabled]),[tabindex]:not([tabindex="-1"])';
  const lockScroll = (locked) => {
    document.documentElement.classList.toggle('cm-flora-scroll-locked', locked);
    document.body.classList.toggle('cm-flora-scroll-locked', locked);
  };

  class FloraDialog extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized) return;
      this.dataset.initialized = 'true';
      this.dialog = this.querySelector('dialog');
      this.trigger = document.querySelector(`[aria-controls="${this.id}"]`);
      if (!this.dialog) return;
      this.addEventListener('click', (event) => { if (event.target === this.dialog) this.close(); });
      this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); });
      this.querySelectorAll('[data-cm-dialog-close]').forEach((button) => button.addEventListener('click', () => this.close()));
    }
    open(trigger) {
      this.trigger = trigger || this.trigger;
      if (!this.dialog || this.dialog.open) return;
      this.dialog.showModal();
      this.classList.add('is-open');
      lockScroll(true);
      requestAnimationFrame(() => (this.querySelector('input[type="search"]') || this.querySelector(FOCUSABLE))?.focus());
    }
    close() {
      if (!this.dialog?.open) return;
      this.dialog.close();
      this.classList.remove('is-open');
      lockScroll(false);
      this.trigger?.focus();
    }
  }

  class FloraPredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized) return;
      this.dataset.initialized = 'true';
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-cm-search-results]');
      this.status = this.querySelector('[data-cm-search-status]');
      this.form = this.querySelector('form');
      this.dialogHost = this.closest('.cm-flora-search-dialog');
      this.emptyMarkup = this.results?.innerHTML || '';
      this.setViewState('empty');
      this.renderEmptyState();
      this.input?.addEventListener('input', this.search.bind(this));
      this.input?.addEventListener('keydown', this.onKeyDown.bind(this));
      this.form?.addEventListener('submit', () => this.abortController?.abort());
    }
    async search() {
      const term = this.input.value.trim();
      if (!term) {
        this.setViewState('empty');
        this.renderEmptyState();
        this.input.setAttribute('aria-expanded', 'false');
        this.status.textContent = '';
        return;
      }
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.setViewState('results');
      this.input.setAttribute('aria-expanded', 'true');
      this.status.textContent = 'Loading search results';
      this.results.innerHTML = '<p class="cm-flora-search-loading">Loading...</p>';
      const endpoint = window.routes?.predictive_search_url || '/search/suggest';
      const url = `${endpoint}?q=${encodeURIComponent(term)}&resources[type]=product,collection,page,article,query&resources[limit]=8&resources[options][unavailable_products]=hide`;
      try {
        const response = await fetch(url, { signal: this.abortController.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Search request failed');
        const resources = (await response.json()).resources?.results || {};
        const products = (resources.products || []).slice(0, 4);
        const suggestions = [
          ...(resources.queries || []).map((query) => ({ title: query.text, url: query.url || `/search?q=${encodeURIComponent(query.text || '')}`, type: 'Search' })),
          ...(resources.collections || []).map((collection) => ({ title: collection.title, url: collection.url, type: 'Collection' })),
          ...(resources.pages || []).map((page) => ({ title: page.title, url: page.url, type: 'Page' })),
          ...(resources.articles || []).map((article) => ({ title: article.title, url: article.url, type: 'Journal' }))
        ].slice(0, 4);
        this.results.innerHTML = products.length || suggestions.length
          ? this.resultsMarkup(suggestions, products)
          : '<p class="cm-flora-search-empty">No results found.</p>';
        this.status.textContent = `${products.length + suggestions.length} search results`;
      } catch (error) {
        if (error.name !== 'AbortError') { this.results.innerHTML = '<p class="cm-flora-search-empty">Search is temporarily unavailable.</p>'; this.status.textContent = 'Search unavailable'; }
      }
    }
    setViewState(state) {
      this.dialogHost?.classList.toggle('is-empty', state === 'empty');
      this.dialogHost?.classList.toggle('has-results', state === 'results');
    }
    renderEmptyState() { if (this.results) this.results.innerHTML = this.emptyMarkup; }
    resultsMarkup(suggestions, products) {
      const suggestionMarkup = suggestions.length
        ? `<ul class="cm-flora-search__suggestion-list" role="list">${suggestions.map((suggestion) => this.suggestionMarkup(suggestion)).join('')}</ul>`
        : '<p class="cm-flora-search-empty">No matching suggestions.</p>';
      const productMarkup = products.length
        ? `<ul class="cm-flora-search__product-list" role="list">${products.map((product) => `<li>${this.productMarkup(product)}</li>`).join('')}</ul>`
        : '<p class="cm-flora-search-empty">No matching products.</p>';
      return `<div class="cm-flora-search__results-grid"><section aria-labelledby="cm-search-suggestions-heading"><h2 id="cm-search-suggestions-heading" class="cm-flora-search__section-heading">Suggestions</h2>${suggestionMarkup}</section><section aria-labelledby="cm-search-products-heading"><h2 id="cm-search-products-heading" class="cm-flora-search__section-heading">Products</h2>${productMarkup}</section></div>`;
    }
    suggestionMarkup(suggestion) {
      return `<li><a href="${this.escape(suggestion.url)}"><span>${this.escape(suggestion.title)}</span><small>${this.escape(suggestion.type)}</small></a></li>`;
    }
    productMarkup(product) {
      const image = product.image ? `<img src="${this.escape(product.image)}" alt="${this.escape(product.title)}" loading="lazy" decoding="async" width="80" height="80">` : '';
      const price = product.price ? `<span>${product.price}</span>` : '';
      return `<a class="cm-flora-search-result" href="${this.escape(product.url)}">${image}<span><strong>${this.escape(product.title)}</strong>${price}</span></a>`;
    }
    escape(value) { const node = document.createElement('span'); node.textContent = value || ''; return node.innerHTML; }
    onKeyDown(event) {
      const links = [...this.results.querySelectorAll('a')];
      if (event.key === 'ArrowDown' && links.length) { event.preventDefault(); links[0].focus(); }
    }
  }

  const initializeHeaderMenu = (menu) => {
    if (!menu || menu.dataset.cmMenuBound === 'true') return;
    menu.dataset.cmMenuBound = 'true';
    let openItem = null;
    let closeTimer;
    const isDesktop = () => window.matchMedia('(min-width: 990px)').matches;
    const region = menu.closest('.cm-flora-header-shell') || menu;
    const setState = (trigger, open) => {
      const panelId = trigger?.getAttribute('aria-controls');
      const panel = panelId ? document.getElementById(panelId) : null;
      trigger?.setAttribute('aria-expanded', String(open));
      panel?.toggleAttribute('hidden', !open);
      panel?.classList.toggle('is-open', open);
    };
    const close = () => {
      clearTimeout(closeTimer);
      if (!openItem) return;
      setState(openItem, false);
      openItem = null;
    };
    const open = (trigger) => {
      if (!trigger || trigger.dataset.hasMenu !== 'true') return;
      clearTimeout(closeTimer);
      if (openItem && openItem !== trigger) setState(openItem, false);
      openItem = trigger;
      setState(trigger, true);
    };
    const scheduleClose = () => {
      clearTimeout(closeTimer);
      closeTimer = setTimeout(close, 160);
    };

    menu.addEventListener('click', (event) => {
      const trigger = event.target instanceof Element ? event.target.closest('[data-menu-trigger]') : null;
      if (!trigger || !menu.contains(trigger) || trigger.dataset.hasMenu !== 'true') return;
      if (trigger.tagName === 'A' && trigger.getAttribute('href')) return;
      event.preventDefault();
      openItem === trigger ? close() : open(trigger);
    });
    menu.addEventListener('keydown', (event) => {
      const trigger = event.target instanceof Element ? event.target.closest('[data-menu-trigger]') : null;
      if (!trigger || !menu.contains(trigger)) return;
      if (event.key === 'ArrowDown' && trigger.dataset.hasMenu === 'true') {
        event.preventDefault();
        open(trigger);
        document.getElementById(trigger.getAttribute('aria-controls'))?.querySelector('a')?.focus();
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        trigger.focus();
      }
    });
    region.addEventListener('pointerover', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const trigger = target?.closest('[data-menu-trigger]');
      const panel = target?.closest('.cm-flora-menu__panel');
      if (panel && menu.contains(panel)) clearTimeout(closeTimer);
      if (!trigger || !menu.contains(trigger) || !isDesktop()) return;
      if (event.relatedTarget instanceof Node && trigger.contains(event.relatedTarget)) return;
      open(trigger);
    });
    region.addEventListener('pointerleave', () => { if (isDesktop()) scheduleClose(); });
    menu.querySelectorAll('.cm-flora-menu__panel').forEach((panel) => {
      panel.addEventListener('pointerenter', () => clearTimeout(closeTimer));
    });
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const trigger = openItem;
      close();
      trigger?.focus();
    });
  };

  class FloraHeaderMenu extends HTMLElement {
    connectedCallback() { initializeHeaderMenu(this); }
  }

  class FloraDrawer extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized) return;
      this.dataset.initialized = 'true';
      this.trigger = document.querySelector('[data-cm-drawer-open]');
      this.mainMenu = this.querySelector('[data-cm-drawer-main]');
      this.submenuPanel = this.querySelector('[data-cm-drawer-subpanel]');
      this.submenuContent = this.querySelector('[data-cm-drawer-submenu-content]');
      this.submenuTitle = this.querySelector('[data-cm-drawer-back-title]');
      this.trigger?.addEventListener('click', () => this.open());
      this.querySelectorAll('[data-cm-drawer-close]').forEach((button) => button.addEventListener('click', () => this.close()));
      this.addEventListener('click', (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const submenuTrigger = target?.closest('[data-cm-drawer-submenu]');
        const backButton = target?.closest('[data-cm-drawer-back]');

        if (submenuTrigger && this.mainMenu?.contains(submenuTrigger)) {
          event.preventDefault();
          this.showSubmenu(submenuTrigger);
          return;
        }
        if (backButton && this.contains(backButton)) {
          event.preventDefault();
          this.hideSubmenu(backButton);
          return;
        }
        if (event.target === this) this.close();
      });
      this.addEventListener('keydown', (event) => { if (event.key === 'Escape') this.close(); });
    }
    open() {
      this.resetSubmenu();
      this.setAnnouncementOffset();
      this.hidden = false;
      requestAnimationFrame(() => this.classList.add('is-open'));
      lockScroll(true);
      this.querySelector(FOCUSABLE)?.focus();
    }
    setAnnouncementOffset() {
      if (!window.matchMedia('(max-width: 989px)').matches) return;
      const announcement = document.querySelector('.announcement-bar-section');
      const bounds = announcement?.getBoundingClientRect();
      const offset = bounds && bounds.bottom > 0 ? Math.min(bounds.bottom, window.innerHeight) : 0;
      this.style.setProperty('--cm-announcement-height', `${Math.max(0, offset)}px`);
    }
    close() {
      this.classList.remove('is-open');
      this.resetSubmenu();
      lockScroll(false);
      setTimeout(() => { this.hidden = true; }, 220);
      this.trigger?.focus();
    }
    resetSubmenu() {
      if (this.mainMenu) this.mainMenu.hidden = false;
      if (this.submenuPanel) this.submenuPanel.hidden = true;
      this.submenuContent?.replaceChildren();
      if (this.submenuTitle) this.submenuTitle.textContent = '';
      this.activeSubmenu = null;
    }
    showSubmenu(button) {
      const menuId = button.dataset.cmDrawerSubmenuId;
      const templateId = button.dataset.cmDrawerSubmenuTemplate;
      const template = templateId ? document.getElementById(templateId) : null;
      if (!menuId || !template || !this.contains(template) || !this.submenuContent || !this.submenuPanel) return;
      this.resetSubmenu();
      const fragment = template.content.cloneNode(true);
      const items = [...fragment.children];
      this.submenuContent.replaceChildren(fragment);
      this.activeSubmenu = {
        id: menuId,
        title: button.dataset.cmDrawerSubmenuTitle || button.textContent.trim(),
        items,
        parent: this.mainMenu,
        panel: this.submenuPanel,
        template,
        trigger: button,
      };
      this.submenuTitle.textContent = this.activeSubmenu.title;
      this.mainMenu.hidden = true;
      this.submenuPanel.hidden = false;
      this.submenuPanel.querySelector(FOCUSABLE)?.focus();
    }
    hideSubmenu(button) {
      const activeSubmenu = this.activeSubmenu;
      const panel = button.closest('[data-cm-drawer-subpanel]');
      if (!activeSubmenu || panel !== this.submenuPanel) return;
      this.resetSubmenu();
      activeSubmenu.trigger?.focus();
    }
  }

  class FloraAccount extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized) return;
      this.dataset.initialized = 'true';
      this.trigger = this.querySelector('[data-cm-account-trigger]');
      this.panel = this.querySelector('[data-cm-account-panel]');
      this.trigger?.addEventListener('click', () => this.toggle());
      document.addEventListener('click', (event) => { if (!this.contains(event.target)) this.close(); });
      this.addEventListener('keydown', (event) => { if (event.key === 'Escape') { this.close(); this.trigger?.focus(); } });
    }
    toggle() { this.openState ? this.close() : this.open(); }
    open() { this.openState = true; this.trigger?.setAttribute('aria-expanded', 'true'); this.panel?.removeAttribute('hidden'); this.panel?.querySelector(FOCUSABLE)?.focus(); }
    close() { this.openState = false; this.trigger?.setAttribute('aria-expanded', 'false'); this.panel?.setAttribute('hidden', ''); }
  }

  class FloraHeader extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized) return;
      this.dataset.initialized = 'true';
      this.shell = this.closest('.cm-flora-header-shell');
      this.onScroll = () => {
        const isDesktop = window.matchMedia('(min-width: 990px)').matches;
        this.shell?.classList.toggle('is-scrolled', isDesktop && window.scrollY > 8);
      };
      if (this.dataset.sticky !== 'none') {
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();
      }
    }
  }

  if (!customElements.get('dialog-component')) customElements.define('dialog-component', FloraDialog);
  if (!customElements.get('predictive-search-component')) customElements.define('predictive-search-component', FloraPredictiveSearch);
  if (!customElements.get('cm-header-menu')) customElements.define('cm-header-menu', FloraHeaderMenu);
  if (!customElements.get('cm-flora-drawer')) customElements.define('cm-flora-drawer', FloraDrawer);
  if (!customElements.get('cm-flora-account')) customElements.define('cm-flora-account', FloraAccount);
  if (!customElements.get('header-component')) customElements.define('header-component', FloraHeader);
  document.querySelectorAll('cm-header-menu').forEach(initializeHeaderMenu);
  document.addEventListener('click', (event) => {
    const searchTrigger = event.target.closest('[data-cm-search-open]');
    if (searchTrigger) { event.preventDefault(); document.getElementById(searchTrigger.getAttribute('aria-controls'))?.open(searchTrigger); }
    const accountTrigger = event.target.closest('[data-cm-account-open]');
    if (accountTrigger) {
      event.preventDefault();
      document.querySelector('cm-flora-drawer')?.close();
      document.querySelector('cm-flora-account')?.open();
    }
  });
})();
